import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import { 
  TrendingUp, 
  TrendingDown, 
  Clock, 
  DollarSign, 
  Activity, 
  RefreshCw
} from 'lucide-react';
import { apiUrl } from "../config/api";
import { adminAuthHeaders } from "../utils/authHeaders";

const ProfessionalChart = () => {
  // --- State Management ---
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [livePrices, setLivePrices] = useState({ buy: 0, sell: 0 });
  const [lastUpdate, setLastUpdate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // Refs for polling interval to avoid stale closures
  const lastSavedRef = useRef({ buy: null, sell: null });
  const pollingIntervalRef = useRef(null);
  const initializedRef = useRef(false); // جلوگیری از اجرای مکرر

  // --- Data Loading Logic ---

  // Helper to format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fa-IR').format(value);
  };

  // کلید localStorage بر اساس آیتم انتخابی
  const getStorageKey = () => {
    return selectedItem ? `chartData_${selectedItem.id}` : null;
  };

  const tradeableCategories = useMemo(() => (
    categories
      .map((category) => ({
        ...category,
        items: (category.items || []).filter((item) => item.trade),
      }))
      .filter((category) => category.items.length > 0)
  ), [categories]);

  const selectedCategoryTradeableItems = useMemo(() => (
    (selectedCategory?.items || []).filter((item) => item.trade)
  ), [selectedCategory]);

  const showCategoryFilter = tradeableCategories.length > 1;
  const showItemFilter = selectedCategoryTradeableItems.length > 1;
  const showFilters = showCategoryFilter || showItemFilter;

  // 1. Load Categories on Mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(apiUrl('/api/categories'));
        if (!res.ok) throw new Error('Failed to fetch categories');
        const data = await res.json();
        setCategories(data);
        // ✅ انتخاب خودکار اولین دسته‌بندی و آیتم (فقط یکبار)
        if (!initializedRef.current && data.length > 0) {
          const firstCategory = data.find(category => (category.items || []).some(item => item.trade));
          setSelectedCategory(firstCategory || null);
          // پیدا کردن اولین آیتم قابل معامله
          const firstTradeableItem = firstCategory?.items.find(item => item.trade);
          if (firstTradeableItem) {
            setSelectedItem(firstTradeableItem);
          } else {
            setSelectedItem(null);
          }
          initializedRef.current = true;
        }
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  // 2. Load History when Item Changes
  useEffect(() => {
    const checkDataLoaded = () => {
      // بررسی اینکه آیا داده‌ها لود شده‌اند
      const pricesLoaded = categories.length > 0;
      const chartLoaded = chartData.length > 0;
      
      if (pricesLoaded && chartLoaded) {
        setIsLoading(false);
      }
    };
    // چک کردن هر 500ms
    const interval = setInterval(checkDataLoaded, 500);
    return () => clearInterval(interval);
  }, [categories, chartData]);

  useEffect(() => {
    if (selectedItem) {
      loadHistory(selectedItem.title);
    } else {
      setChartData([]);
      setLivePrices({ buy: 0, sell: 0 });
    }
  }, [selectedItem]);

  const loadHistory = async (itemName) => {
    try {
      // ابتدا از localStorage داده‌ها را بارگذاری کن
      const storageKey = getStorageKey();
      if (storageKey) {
        const savedData = localStorage.getItem(storageKey);
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          if (parsedData.length > 0) {
            setChartData(parsedData);
            const lastPoint = parsedData[parsedData.length - 1];
            lastSavedRef.current = { buy: lastPoint.buy, sell: lastPoint.sell };
            setLivePrices({ buy: lastPoint.buy, sell: lastPoint.sell });
            setLastUpdate(new Date(lastPoint.timestamp || Date.now()));
            return;
          }
        }
      }
      // اگر داده‌ای در localStorage نبود، از API بگیر
      const res = await fetch(apiUrl(`/api/history/${itemName}`));
      if (!res.ok) throw new Error('Failed to fetch history');
      const data = await res.json();
      setChartData(data);
      if (data.length > 0) {
        const lastPoint = data[data.length - 1];
        lastSavedRef.current = { buy: lastPoint.buy, sell: lastPoint.sell };
        setLivePrices({ buy: lastPoint.buy, sell: lastPoint.sell });
        setLastUpdate(new Date(lastPoint.timestamp || Date.now()));
        // ذخیره در localStorage
        const storageKey = getStorageKey();
        if (storageKey) {
          localStorage.setItem(storageKey, JSON.stringify(data));
        }
      }
    } catch (err) {
      console.error("History load error:", err);
    }
  };

  // ذخیره داده‌ها در localStorage
  const saveToLocalStorage = (newData) => {
    const storageKey = getStorageKey();
    if (storageKey) {
      localStorage.setItem(storageKey, JSON.stringify(newData));
    }
  };

  // 3. Polling Logic (Check for price changes every 5 seconds)
  useEffect(() => {
    if (!selectedItem) return;
    
    pollingIntervalRef.current = setInterval(async () => {
      try {
        // Fetch latest live data from your existing endpoint
        const res = await fetch(apiUrl('/api/categories'));
        const data = await res.json();
        const category = data.find(c => c.name === selectedCategory.name);
        if (!category) return;
        const item = category.items.find(i => i.id === selectedItem.id);
        if (!item || !item.trade) return;
        
        const newBuy = parseFloat(item.valueBuy) || 0;
        const newSell = parseFloat(item.valueSell) || 0;

        // Check if prices changed compared to last saved
        if (lastSavedRef.current.buy !== newBuy || lastSavedRef.current.sell !== newSell) {
          const now = new Date();
          const timeString = now.toLocaleTimeString('fa-IR');
          
          // Update Live UI
          setLivePrices({ buy: newBuy, sell: newSell });
          setLastUpdate(now);

          // Add to Chart Data
          setChartData(prevData => {
            const newDataPoint = {
              time: timeString,
              buy: newBuy,
              sell: newSell,
              timestamp: now.toISOString()
            };
            // Keep last 30 points for performance
            const newData = [...prevData, newDataPoint];
            let finalData = newData;
            
            if (newData.length > 30) {
              finalData = newData.slice(newData.length - 30);
            }
            // ذخیره در localStorage
            saveToLocalStorage(finalData);
            return finalData;
          });

          // Save to Symfony Backend
          await fetch(apiUrl('/api/save-price'), {
            method: 'POST',
            headers: adminAuthHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({
              itemName: selectedItem.title,
              buyPrice: newBuy,
              sellPrice: newSell
            })
          });

          // Update Ref
          lastSavedRef.current = { buy: newBuy, sell: newSell };
        }
      } catch (err) {
        console.error("Polling error:", err);
      }
    }, 5000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [selectedItem, selectedCategory]);

  // --- UI Rendering ---

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-900 rounded-xl">
        <RefreshCw className="animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-900/20 border border-red-500/50 rounded-xl text-red-400">
        خطا در بارگذاری داده‌ها: {error}
      </div>
    );
  }

  return (
    <div className="chart TradingChart">
      <div className="TradingChartTopbar">
        <div className="TradingChartIdentity">
          <span className="TradingChartIcon">
            <Activity size={18} />
          </span>
          <div>
            <span className="TradingChartEyebrow">LIVE MARKET</span>
            <h3 className="TradingChartTitle">{selectedItem?.title || "نمودار قیمت"}</h3>
          </div>
        </div>

      {/* Header Section */}
        {/* Selectors */}
        <div className={`HeaderSelector ${showFilters ? "" : "ChartFilterHidden"} ${showCategoryFilter && showItemFilter ? "" : "single"}`}>
          <div className={`Selector ${showCategoryFilter ? "" : "ChartFilterHidden"}`}>
            <select
              value={selectedCategory?.name || ''}
              onChange={(e) => {
                const cat = tradeableCategories.find(c => c.name === e.target.value);
                setSelectedCategory(cat || null);
                // ✅ هنگام تغییر دسته، اولین آیتم قابل معامله را انتخاب کن
                const firstTradeableItem = cat?.items.find(item => item.trade);
                setSelectedItem(firstTradeableItem || null);
              }}
              className="appearance-none bg-gray-800 text-white border border-gray-600 rounded-lg px-4 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
            >
              <option value="">انتخاب دسته‌بندی</option>
              {tradeableCategories.map((cat) => (
                <option key={cat.id} value={cat.name}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
          <div className={`Selector ${showItemFilter ? "" : "ChartFilterHidden"}`}>
            <select
              value={selectedItem?.title || ''}
              onChange={(e) => {
                const item = selectedCategoryTradeableItems.find(i => i.title === e.target.value);
                setSelectedItem(item || null);
              }}
              disabled={!selectedCategory}
              className="appearance-none bg-gray-800 text-white border border-gray-600 rounded-lg px-4 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">انتخاب آیتم</option>
              {selectedCategoryTradeableItems.map((item) => (
                <option key={item.id} value={item.title}>
                  {item.title}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {selectedItem && (
        <div className="TradingChartStats">
          <div className="TradingChartStat buy">
            <span><TrendingUp size={15} /> قیمت خرید</span>
            <strong>{formatCurrency(livePrices.buy || 0)}</strong>
          </div>
          <div className="TradingChartStat sell">
            <span><TrendingDown size={15} /> قیمت فروش</span>
            <strong>{formatCurrency(livePrices.sell || 0)}</strong>
          </div>
          <div className="TradingChartStat time">
            <span><Clock size={15} /> آخرین بروزرسانی</span>
            <strong>{lastUpdate ? lastUpdate.toLocaleTimeString('fa-IR') : '--:--'}</strong>
          </div>
        </div>
      )}

      <div className="TradingChartToolbar">
        <button type="button" className="active">Live</button>
        <button type="button">1D</button>
        <button type="button">1W</button>
        <button type="button">1M</button>
        <span>نمایش ۳۰ نقطه آخر بازار</span>
      </div>

      {/* Live Price Cards */}
      {selectedItem && (
        <div className="">
          {/* Last Update Card */}
          <div className="LastUpdate mt-2">
            <div className="TitleChart">
              {selectedItem.title}
            </div>
            <div className="LastSeen">
              <span className="">آخرین بروزرسانی</span>
            </div>
            <div className="LastTime">
              {lastUpdate ? lastUpdate.toLocaleTimeString('fa-IR') : '--:--'}
            </div>
          </div>
        </div>
      )}

      {/* Chart Area */}
      <div className="ChartContainer TradingViewChartSurface">
        {selectedItem ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={chartData}
              margin={{ top: 18, right: 18, left: -40, bottom: 4 }}
            >
              <defs>
                <linearGradient id="colorBuy" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1bd895" stopOpacity={0.42}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorSell" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ff4d6d" stopOpacity={0.34}/>
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <XAxis 
                dataKey="time" 
                stroke="#6f7a8a" 
                fontSize={12} 
                tickLine={false}
                axisLine={false}
                minTickGap={26}
              />
              <YAxis 
                stroke="#6f7a8a" 
                fontSize={12} 
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `${value / 1000}k`}
                orientation="left"
              />
              <CartesianGrid strokeDasharray="2 8" stroke="rgba(122, 138, 166, 0.2)" vertical />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(10, 16, 27, 0.94)', 
                  borderColor: 'rgba(93, 112, 143, 0.55)',
                  borderRadius: '12px',
                  color: '#eef4ff',
                  boxShadow: '0 18px 48px rgba(0, 0, 0, 0.36)',
                  backdropFilter: 'blur(14px)'
                }}
                cursor={{ stroke: 'rgba(228, 187, 115, 0.42)', strokeWidth: 1, strokeDasharray: '4 4' }}
                itemStyle={{ color: '#eef4ff' }}
                formatter={(value, name) => [
                  formatCurrency(value || 0), 
                  name === 'buy' ? 'قیمت خرید' : 'قیمت فروش'
                ]}
                labelStyle={{ color: '#9ca3af', marginBottom: '0.5rem' }}
              />
              <Area
                type="monotone"
                dataKey="buy"
                name="خرید"
                stroke="#1bd895"
                strokeWidth={3}
                fillOpacity={1}
                fill="url(#colorBuy)"
                animationDuration={1000}
              />
              <Area
                type="monotone"
                dataKey="sell"
                name="فروش"
                stroke="#ff4d6d"
                strokeWidth={2.5}
                fillOpacity={1}
                fill="url(#colorSell)"
                animationDuration={1000}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-gray-500">
            <DollarSign className="w-16 h-16 mb-4 opacity-20" />
            <p>لطفاً یک دسته‌بندی و آیتم را برای مشاهده نمودار انتخاب کنید</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfessionalChart;
